import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { generateUUID } from "@/lib/utils/uuid";
import { sanitizeFilenameForStorage } from "@/lib/utils/filename";
import type { TermTemplateConfig } from "@/lib/terms/config";
import { normalizeTermTemplateConfig } from "@/lib/terms/config";
import i18n from "@/i18n";
import { useProfile } from "@/hooks/useProfile";
import { resolveHistoryClinicScope } from "@/lib/clinics/visible-clinics";

export type TermTemplate = {
  id: string;
  clinic_id?: string | null;
  customer_id: string | null;
  owner_user_id: string;
  name: string;
  content: string;
  config_json: unknown;
  version: number;
  is_active: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  deleted_by?: string | null;
};

export type PatientTerm = {
  id: string;
  clinic_id?: string | null;
  pet_id: string;
  medical_record_id: string | null;
  template_id: string | null;
  title: string;
  printed_at: string | null;
  signed_uploaded_at: string | null;
  signed_storage_bucket: string | null;
  signed_storage_path: string | null;
  signed_file_name: string | null;
  signed_mime_type: string | null;
  signed_file_size: number | null;
  rendered_title: string;
  rendered_content: string;
  field_values_json: unknown;
  resolved_values_json: unknown;
  template_snapshot_name: string;
  template_snapshot_content: string;
  template_snapshot_config: unknown;
  printed_by: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  template?: Pick<TermTemplate, "id" | "name"> | null;
  // Digital signature fields (added via migration)
  digitally_signed_at?: string | null;
  digitally_signed_by_user_id?: string | null;
  digitally_signed_storage_bucket?: string | null;
  digitally_signed_storage_path?: string | null;
  digitally_signed_pdf_sha256?: string | null;
  digitally_signed_access_code?: string | null;
};

export type CreateTermTemplatePayload = {
  name: string;
  content: string;
  is_active?: boolean;
  config_json?: TermTemplateConfig;
};

export type CreatePatientTermPayload = {
  clinic_id: string;
  pet_id: string;
  medical_record_id?: string | null;
  template_id?: string | null;
  title: string;
  field_values_json?: Record<string, string>;
  resolved_values_json?: Record<string, string>;
  template_snapshot_name?: string;
  template_snapshot_content?: string;
  template_snapshot_config?: TermTemplateConfig;
  rendered_title?: string;
  rendered_content?: string;
};

const invalidateTermHistoryQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  petId?: string | null,
) => {
  if (petId) {
    queryClient.invalidateQueries({
      queryKey: ["patient-terms", petId],
      refetchType: "active",
    });
  }
  queryClient.invalidateQueries({
    queryKey: ["patient-history", "multi-pet"],
    refetchType: "active",
  });
  queryClient.invalidateQueries({
    queryKey: ["patient-history-feed"],
    refetchType: "active",
  });
  queryClient.invalidateQueries({
    queryKey: ["owner-history-timeline"],
    refetchType: "active",
  });
  queryClient.invalidateQueries({
    queryKey: ["owner-history-feed"],
    refetchType: "active",
  });
};

const upsertPatientTermInCache = (current: PatientTerm[] | undefined, next: PatientTerm) => {
  if (!current) return [next];
  const filtered = current.filter((item) => item.id !== next.id);
  return [next, ...filtered].sort((a, b) => {
    const left = new Date(a.created_at ?? 0).getTime();
    const right = new Date(b.created_at ?? 0).getTime();
    return right - left;
  });
};

export const useTermTemplates = (onlyActive = false, clinicIdOverride?: string | null) => {
  const { data: profile } = useProfile();
  const clinicId = clinicIdOverride ?? profile?.current_clinic_id ?? null;

  return useQuery({
    queryKey: ["term-templates", onlyActive, clinicId],
    enabled: Boolean(clinicId),
    queryFn: async () => {
      if (!clinicId) return [] as TermTemplate[];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let query = (supabase as any)
        .from("customer_term_templates")
        .select("*")
        .eq("clinic_id", clinicId)
        .is("deleted_at", null)
        .order("name", { ascending: true });

      if (onlyActive) {
        query = query.eq("is_active", true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as TermTemplate[];
    },
  });
};

export const useCreateTermTemplate = () => {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();

  return useMutation({
    mutationFn: async (payload: CreateTermTemplatePayload) => {
      const { data: authData } = await supabase.auth.getUser();

      const userId = authData.user?.id;
      if (!userId) throw new Error(i18n.t("terms:templates.feedback.notAuthenticated"));
      if (!profile?.current_clinic_id) throw new Error(i18n.t("terms:templates.feedback.createError"));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("customer_term_templates")
        .insert({
          clinic_id: profile.current_clinic_id,
          owner_user_id: userId,
          name: payload.name.trim(),
          content: payload.content.trim(),
          config_json: normalizeTermTemplateConfig(payload.config_json),
          version: 1,
          is_active: payload.is_active ?? true,
          created_by: userId,
        })
        .select("*")
        .single();

      if (error) throw error;
      return data as TermTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["term-templates"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["user-permissions-map"], refetchType: "all" });
      toast.success(i18n.t("terms:templates.feedback.created"));
    },
    onError: (error) => {
      console.error("Erro ao criar modelo de termo:", error);
      toast.error(i18n.t("terms:templates.feedback.createError"));
    },
  });
};

export const useUpdateTermTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: { name: string; content: string; is_active: boolean; config_json?: TermTemplateConfig };
    }) => {
      const updatePayload: Record<string, unknown> = {
        name: payload.name.trim(),
        content: payload.content.trim(),
        is_active: payload.is_active,
      };
      if (payload.config_json !== undefined) {
        updatePayload.config_json = normalizeTermTemplateConfig(payload.config_json);
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("customer_term_templates")
        .update(updatePayload)
        .eq("id", templateId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["term-templates"], refetchType: "all" });
      toast.success(i18n.t("terms:templates.feedback.updated"));
    },
    onError: (error) => {
      console.error("Erro ao atualizar modelo de termo:", error);
      toast.error(i18n.t("terms:templates.feedback.updateError"));
    },
  });
};

export const useDeleteTermTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateId: string) => {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) throw new Error(i18n.t("terms:templates.feedback.notAuthenticated"));

      // Soft delete
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("customer_term_templates")
        .update({ deleted_at: new Date().toISOString(), deleted_by: userId })
        .eq("id", templateId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["term-templates"], refetchType: "all" });
      toast.success(i18n.t("terms:templates.feedback.deleted"));
    },
    onError: (error) => {
      console.error("Erro ao remover modelo de termo:", error);
      toast.error(i18n.t("terms:templates.feedback.deleteError"));
    },
  });
};

export const usePatientTerms = (
  petId?: string,
  medicalRecordId?: string,
  clinicId?: string | null,
  options?: { enabled?: boolean; clinicIds?: string[] | null },
) => {
  // Histórico consolidado: quando o chamador fornece o conjunto de clínicas
  // visíveis, lê de todas elas; senão, mantém o escopo da clínica única.
  const clinicScope = resolveHistoryClinicScope(options?.clinicIds, clinicId);
  return useQuery({
    queryKey: ["patient-terms", petId, medicalRecordId ?? "all", clinicScope ?? null],
    enabled: Boolean(petId) && Boolean(clinicScope) && (options?.enabled ?? true),
    queryFn: async () => {
      if (!petId || !clinicScope) return [] as PatientTerm[];

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let query = (supabase as any)
        .from("patient_terms")
        .select("*, template:customer_term_templates(id,name)")
        .in("clinic_id", clinicScope)
        .eq("pet_id", petId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (medicalRecordId) {
        query = query.eq("medical_record_id", medicalRecordId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as PatientTerm[];
    },
  });
};

export const useCreatePatientTerm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreatePatientTermPayload) => {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) throw new Error("Usuário não autenticado.");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("patient_terms")
        .insert({
          clinic_id: payload.clinic_id,
          pet_id: payload.pet_id,
          medical_record_id: payload.medical_record_id ?? null,
          template_id: payload.template_id ?? null,
          title: payload.title.trim(),
          rendered_title: payload.rendered_title ?? payload.title.trim(),
          rendered_content: payload.rendered_content ?? "",
          field_values_json: payload.field_values_json ?? {},
          resolved_values_json: payload.resolved_values_json ?? {},
          template_snapshot_name: payload.template_snapshot_name ?? "",
          template_snapshot_content: payload.template_snapshot_content ?? "",
          template_snapshot_config: normalizeTermTemplateConfig(payload.template_snapshot_config),
          created_by: userId,
        })
        .select("*")
        .single();

      if (error) throw error;
      return data as PatientTerm;
    },
    onSuccess: (created) => {
      queryClient.setQueryData<PatientTerm[]>(
        ["patient-terms", created.pet_id, created.medical_record_id ?? "all", created.clinic_id ?? null],
        (current) => upsertPatientTermInCache(current, created),
      );
      queryClient.setQueryData<PatientTerm[]>(
        ["patient-terms", created.pet_id, "all", created.clinic_id ?? null],
        (current) => upsertPatientTermInCache(current, created),
      );
      queryClient.invalidateQueries({ queryKey: ["patient-terms", created.pet_id] });
      invalidateTermHistoryQueries(queryClient, created.pet_id);
      console.info("[terms] created", {
        termId: created.id,
        petId: created.pet_id,
        medicalRecordId: created.medical_record_id,
        clinicId: created.clinic_id ?? null,
      });
      toast.success("Termo criado com sucesso.");
    },
    onError: (error) => {
      console.error("Erro ao criar termo:", error);
      toast.error("Não foi possível criar o termo.");
    },
  });
};

export const useMarkTermPrinted = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { termId: string; printedAt?: string | null }) => {
      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id ?? null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("patient_terms")
        .update({ printed_at: payload.printedAt ?? new Date().toISOString(), printed_by: userId })
        .eq("id", payload.termId)
        .select("pet_id")
        .single();
      if (error) throw error;
      return data as { pet_id: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["patient-terms", data.pet_id] });
      invalidateTermHistoryQueries(queryClient, data.pet_id);
    },
    onError: (error) => {
      console.error("Erro ao marcar termo como impresso:", error);
      toast.error("Não foi possível registrar a impressão.");
    },
  });
};

export const useUploadTermSignedFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      termId: string;
      petId: string;
      clinicId: string;
      file: File;
    }) => {
      const safeName = sanitizeFilenameForStorage(payload.file.name);
      const filePath = `${payload.clinicId}/${payload.petId}/${payload.termId}/${generateUUID()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("patient-terms")
        .upload(filePath, payload.file, {
          contentType: payload.file.type,
          upsert: true,
        });

      if (uploadError) throw uploadError;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("patient_terms")
        .update({
          signed_uploaded_at: new Date().toISOString(),
          signed_storage_bucket: "patient-terms",
          signed_storage_path: filePath,
          signed_file_name: payload.file.name,
          signed_mime_type: payload.file.type,
          signed_file_size: payload.file.size,
        })
        .eq("id", payload.termId)
        .select("pet_id")
        .single();

      if (error) throw error;
      return data as { pet_id: string };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["patient-terms", data.pet_id] });
      toast.success("Arquivo assinado enviado.");
    },
    onError: (error) => {
      console.error("Erro ao enviar termo assinado:", error);
      toast.error("Não foi possível enviar o arquivo assinado.");
    },
  });
};

export const useSoftDeletePatientTerm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (termId: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from("patient_terms")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", termId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-terms"] });
      invalidateTermHistoryQueries(queryClient);
      toast.success("Termo removido.");
    },
    onError: (error) => {
      console.error("Erro ao remover termo:", error);
      toast.error("Não foi possível remover o termo.");
    },
  });
};

export const useUpdatePatientTerm = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      termId: string;
      suppressSuccessToast?: boolean;
      title?: string;
      template_id?: string | null;
      field_values_json?: Record<string, string>;
      resolved_values_json?: Record<string, string>;
      template_snapshot_name?: string;
      template_snapshot_content?: string;
      template_snapshot_config?: TermTemplateConfig;
      rendered_title?: string;
      rendered_content?: string;
    }) => {
      const updatePayload: Record<string, unknown> = {};
      if (payload.title !== undefined) {
        updatePayload.title = payload.title.trim();
      }
      if (payload.template_id !== undefined) {
        updatePayload.template_id = payload.template_id ?? null;
      }
      if (payload.field_values_json !== undefined) {
        updatePayload.field_values_json = payload.field_values_json;
      }
      if (payload.resolved_values_json !== undefined) {
        updatePayload.resolved_values_json = payload.resolved_values_json;
      }
      if (payload.template_snapshot_name !== undefined) {
        updatePayload.template_snapshot_name = payload.template_snapshot_name;
      }
      if (payload.template_snapshot_content !== undefined) {
        updatePayload.template_snapshot_content = payload.template_snapshot_content;
      }
      if (payload.template_snapshot_config !== undefined) {
        updatePayload.template_snapshot_config = normalizeTermTemplateConfig(payload.template_snapshot_config);
      }
      if (payload.rendered_title !== undefined) {
        updatePayload.rendered_title = payload.rendered_title.trim();
      }
      if (payload.rendered_content !== undefined) {
        updatePayload.rendered_content = payload.rendered_content;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("patient_terms")
        .update(updatePayload)
        .eq("id", payload.termId)
        .select("*")
        .single();
      if (error) throw error;
      return data as PatientTerm;
    },
    onSuccess: (data, variables) => {
      // Update the cache immediately so the list reflects the edit before the async refetch completes.
      // This prevents a race condition where openTermView picks up stale rendered_content right after an edit.
      const cacheKeys: [string, string, string, string | null][] = [
        ["patient-terms", data.pet_id, data.medical_record_id ?? "all", data.clinic_id ?? null],
        ["patient-terms", data.pet_id, "all", data.clinic_id ?? null],
      ];
      for (const key of cacheKeys) {
        queryClient.setQueryData<PatientTerm[]>(key, (current) =>
          current ? current.map((t) => (t.id === data.id ? data : t)) : current,
        );
      }
      queryClient.invalidateQueries({ queryKey: ["patient-terms", data.pet_id] });
      invalidateTermHistoryQueries(queryClient, data.pet_id);
      if (!variables.suppressSuccessToast) {
        toast.success("Termo atualizado.");
      }
    },
    onError: (error) => {
      console.error("Erro ao atualizar termo:", error);
      toast.error("Não foi possível atualizar o termo.");
    },
  });
};
